/**
 * Default templates for Zendesk ticket creation
 * These templates are used when creating a new Zendesk action node
 * and support dynamic variables via Liquid templating
 */

export interface ZendeskTicketTemplates {
  title: Record<string, string>;
  description: Record<string, string>;
}

/**
 * Default ticket templates by locale
 * Variables available:
 * - {{parcel.trackingId}} - Tracking number
 * - {{status}} - Current tracking status
 * - {{shop.name}} - Shop name
 * - {{carrier.name}} - Carrier name
 * - {{order.firstName}} {{order.lastName}} - Customer name
 * - {{order.email}} - Customer email
 */
export const DEFAULT_ZENDESK_TICKET_TEMPLATES: ZendeskTicketTemplates = {
  title: {
    en: "Tracking Issue - Order {{parcel.trackingId}}",
    fr: "Problème de suivi - Commande {{parcel.trackingId}}",
    es: "Problema de seguimiento - Pedido {{parcel.trackingId}}",
    de: "Sendungsverfolgungsproblem - Bestellung {{parcel.trackingId}}",
    it: "Problema di tracciamento - Ordine {{parcel.trackingId}}",
    nl: "Trackingprobleem - Bestelling {{parcel.trackingId}}",
    pt: "Problema de rastreamento - Pedido {{parcel.trackingId}}",
    sv: "Spårningsproblem - Beställning {{parcel.trackingId}}",
    fi: "Seurantaongelma - Tilaus {{parcel.trackingId}}",
    no: "Sporingsproblem - Bestilling {{parcel.trackingId}}",
    da: "Sporingsproblem - Ordre {{parcel.trackingId}}",
    pl: "Problem ze śledzeniem - Zamówienie {{parcel.trackingId}}",
  },

  description: {
    en: `There is an issue with the tracking for order {{parcel.trackingId}}.

Current status: {{status}}
Carrier: {{carrier.name}}
Customer: {{order.firstName}} {{order.lastName}} ({{order.email}})

Please investigate and contact the customer if necessary.`,

    fr: `Il y a un problème avec le suivi de la commande {{parcel.trackingId}}.

Statut actuel : {{status}}
Transporteur : {{carrier.name}}
Client : {{order.firstName}} {{order.lastName}} ({{order.email}})

Veuillez enquêter et contacter le client si nécessaire.`,

    es: `Hay un problema con el seguimiento del pedido {{parcel.trackingId}}.

Estado actual: {{status}}
Transportista: {{carrier.name}}
Cliente: {{order.firstName}} {{order.lastName}} ({{order.email}})

Por favor, investigue y contacte al cliente si es necesario.`,

    de: `Es gibt ein Problem mit der Sendungsverfolgung für Bestellung {{parcel.trackingId}}.

Aktueller Status: {{status}}
Spediteur: {{carrier.name}}
Kunde: {{order.firstName}} {{order.lastName}} ({{order.email}})

Bitte untersuchen Sie dies und kontaktieren Sie den Kunden bei Bedarf.`,

    it: `C'è un problema con il tracciamento dell'ordine {{parcel.trackingId}}.

Stato attuale: {{status}}
Corriere: {{carrier.name}}
Cliente: {{order.firstName}} {{order.lastName}} ({{order.email}})

Si prega di indagare e contattare il cliente se necessario.`,

    nl: `Er is een probleem met de tracking van bestelling {{parcel.trackingId}}.

Huidige status: {{status}}
Vervoerder: {{carrier.name}}
Klant: {{order.firstName}} {{order.lastName}} ({{order.email}})

Gelieve dit te onderzoeken en de klant te contacteren indien nodig.`,

    pt: `Há um problema com o rastreamento do pedido {{parcel.trackingId}}.

Status atual: {{status}}
Transportadora: {{carrier.name}}
Cliente: {{order.firstName}} {{order.lastName}} ({{order.email}})

Por favor, investigue e entre em contato com o cliente se necessário.`,

    sv: `Det finns ett problem med spårningen för beställning {{parcel.trackingId}}.

Nuvarande status: {{status}}
Fraktbolag: {{carrier.name}}
Kund: {{order.firstName}} {{order.lastName}} ({{order.email}})

Vänligen undersök och kontakta kunden vid behov.`,

    fi: `Tilauksen {{parcel.trackingId}} seurannassa on ongelma.

Nykyinen tila: {{status}}
Rahdinkuljettaja: {{carrier.name}}
Asiakas: {{order.firstName}} {{order.lastName}} ({{order.email}})

Ole hyvä ja tutki asiaa ja ota yhteyttä asiakkaaseen tarvittaessa.`,

    no: `Det er et problem med sporingen for bestilling {{parcel.trackingId}}.

Gjeldende status: {{status}}
Fraktselskap: {{carrier.name}}
Kunde: {{order.firstName}} {{order.lastName}} ({{order.email}})

Vennligst undersøk og kontakt kunden om nødvendig.`,

    da: `Der er et problem med sporingen af ordre {{parcel.trackingId}}.

Nuværende status: {{status}}
Fragtfirma: {{carrier.name}}
Kunde: {{order.firstName}} {{order.lastName}} ({{order.email}})

Venligst undersøg og kontakt kunden om nødvendigt.`,

    pl: `Wystąpił problem ze śledzeniem zamówienia {{parcel.trackingId}}.

Aktualny status: {{status}}
Przewoźnik: {{carrier.name}}
Klient: {{order.firstName}} {{order.lastName}} ({{order.email}})

Proszę zbadać sprawę i skontaktować się z klientem w razie potrzeby.`,
  },
};

/**
 * Return-domain defaults: same node, but the variables come from the return
 * registry ({{return.*}}, {{customer.*}}) instead of the tracking one.
 */
export const DEFAULT_RETURN_ZENDESK_TICKET_TEMPLATES: ZendeskTicketTemplates = {
  title: {
    en: "Return {{return.orderName}}",
    fr: "Retour {{return.orderName}}",
  },
  description: {
    en: `A return has been created for order {{return.orderName}}.

Customer: {{customer.firstName}} {{customer.lastName}} ({{customer.email}})

Please review the request and contact the customer if needed.`,
    fr: `Un retour a été créé pour la commande {{return.orderName}}.

Client : {{customer.firstName}} {{customer.lastName}} ({{customer.email}})

Merci d'examiner la demande et de contacter le client si besoin.`,
  },
};

/**
 * Get default templates for a specific locale with fallback to English
 */
export function getDefaultTemplatesForLocale(
  locale: string,
  domain: "tracking" | "return" = "tracking",
): {
  title: string;
  description: string;
} {
  const templates =
    domain === "return"
      ? DEFAULT_RETURN_ZENDESK_TICKET_TEMPLATES
      : DEFAULT_ZENDESK_TICKET_TEMPLATES;
  return {
    title: templates.title[locale] || templates.title['en'],
    description: templates.description[locale] || templates.description['en'],
  };
}

/**
 * Get default templates for all available locales
 */
export function getDefaultTemplatesForAllLocales(locales: string[]): ZendeskTicketTemplates {
  const templates: ZendeskTicketTemplates = {
    title: {},
    description: {},
  };

  locales.forEach((locale) => {
    const localeTemplates = getDefaultTemplatesForLocale(locale);
    templates.title[locale] = localeTemplates.title;
    templates.description[locale] = localeTemplates.description;
  });

  return templates;
}
